import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Link } from 'react-router-dom';
import { Navbar, Nav, Container } from 'react-bootstrap';
import { FormProvider } from './context/FormContext';
import { AuthProvider } from './context/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import AgentChat from './components/AgentChat';
import './App.css';
const PlanningPage = lazy(() => import('./pages/PlanningPage'));
const Admin = lazy(() => import('./pages/Admin'));
const CalendrierPage = lazy(() => import('./pages/CalendrierPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const FicheSalariePage = lazy(() => import('./pages/FicheSalariePage'));
const FicheProfilPage = lazy(() => import('./pages/FicheProfilPage'));
const ListeEquipePage = lazy(() => import('./pages/ListeEquipePage'));
const AnalysePlanningPage = lazy(() => import('./pages/AnalysePlanningPage'));
const AnalyseAutoPage = lazy(() => import('./pages/AnalyseAutoPage'));
const ManagerDashboardPage = lazy(() => import('./pages/ManagerDashboardPage'));
const EmployeeDashboardPage = lazy(() => import('./pages/EmployeeDashboardPage'));
const AdminPlanningView = lazy(() => import('./components/AdminPlanningView'));
const GestionUtilisateursPage = lazy(() => import('./pages/GestionUtilisateursPage'));
const PlanningEditPage = lazy(() => import('./pages/PlanningEditPage'));

const LoadingSpinner = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
    <div className="spinner-border" role="status">
      <span className="visually-hidden">Chargement...</span>
    </div>
  </div>
);

/**
 * Layout principal avec barre de navigation
 */
const MainLayout = ({ children }) => (
  <>
    <Navbar bg="dark" variant="dark" expand="lg" sticky="top">
      <Container>
        <Navbar.Brand as={Link} to="/">ArchiaTech-Nocibe</Navbar.Brand>
        <Navbar.Toggle aria-controls="basic-navbar-nav" />
        <Navbar.Collapse id="basic-navbar-nav">
          <Nav className="me-auto">
            <Nav.Link as={Link} to="/">Accueil</Nav.Link>
            <Nav.Link as={Link} to="/admin">Admin</Nav.Link>
            <Nav.Link as={Link} to="/dashboard">Dashboard</Nav.Link>
            <Nav.Link as={Link} to="/planning/modifier">Modifier Planning</Nav.Link>
            <Nav.Link as={Link} to="/gestion-utilisateurs">Gestion des Utilisateurs</Nav.Link>
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
    <main className="py-3">
      <Container>
        {children}
      </Container>
    </main>
  </>
);


/**
 * Composant wrapper pour afficher le chat uniquement sur les pages protégées
 */
const AppContent = () => {
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';

  const PageRoutes = (
    <Routes>
      <Route
        path="/login"
        element={
          <Suspense fallback={<LoadingSpinner />}>
            <LoginPage />
          </Suspense>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <PlanningPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Suspense fallback={<LoadingSpinner />}>
              <Admin />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendrier"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <CalendrierPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <DashboardPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager-espace"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin']}>
            <Suspense fallback={<LoadingSpinner />}>
              <ManagerDashboardPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/employe-espace"
        element={
          <ProtectedRoute allowedRoles={['employee']}>
            <Suspense fallback={<LoadingSpinner />}>
              <EmployeeDashboardPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/salarie/:salarieId"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <FicheSalariePage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profil/:profilId"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <FicheProfilPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/equipe/liste"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <ListeEquipePage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/analyse-planning"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <AnalysePlanningPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/analyse-auto"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <AnalyseAutoPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-planning-view"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Suspense fallback={<LoadingSpinner />}>
              <AdminPlanningView />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/gestion-utilisateurs"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Suspense fallback={<LoadingSpinner />}>
              <GestionUtilisateursPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/planning/modifier"
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            <Suspense fallback={<LoadingSpinner />}>
              <PlanningEditPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
    </Routes>
  );

  return (
    <>
      {isLoginPage ? PageRoutes : <MainLayout>{PageRoutes}</MainLayout>}
      {!isLoginPage && <AgentChat />}
    </>
  );
};

function App() {
  return (
    <FormProvider>
      <AuthProvider>
        <ErrorBoundary>
          <Router>
            <AppContent />
          </Router>
        </ErrorBoundary>
      </AuthProvider>
    </FormProvider>
  );
}

export default App;
